import { ApolloClient, ApolloProvider, DefaultOptions, InMemoryCache } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import config from '@saucelabs/sl-util-config';
import React, { useMemo } from 'react';

import { getObjectKeys } from './utils';

// Setup env var for
const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  },
  mutate: {
    errorPolicy: 'all',
  },
};

const ApolloWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  const client = useMemo(() => (new ApolloClient({
    uri: config.apiBaseUrls.iris,
    cache: new InMemoryCache({
      typePolicies: {
        User: {
          fields: {
            // Parse User.id as a UUID without hashes to match the User service's default return
            // types
            id: (id: string) => id.replace(/-/g, ''),
          },
        },
        Query: {
          fields: {
            // Attempt to pull diffs from cache by id when using a diff filter, but only if that is
            // the only filter, and we have all information in the cache (apollo handles this). Used
            // in build details to prevent additional network requests for values already in cache
            diffs: {
              read(existing, { args, toReference }) {
                if (existing || !args) {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                  return existing;
                }

                const argKeys = getObjectKeys(args).join('.');

                // Ensure we only have the filter keys searching by ID, so we can pull those from
                // cache if they exist
                if (argKeys === 'filter.id.in') {
                  const ids = args.filter.id.in as string[];

                  if (ids.length === 0) {
                    return null;
                  }

                  // If present, return an array of references so Apollo can watch & query the
                  // results
                  return {
                    nodes: ids.map((id) => toReference({
                      __typename: 'Diff',
                      id,
                    })),
                  };
                }

                return null;
              },
            },
          },
        },
      },
    }),
    defaultOptions,
    name: 'frontend',
    assumeImmutableResults: true,
    connectToDevTools: true,
    link: new BatchHttpLink({
      uri: config.apiBaseUrls.iris,
      credentials: 'include',
      headers: {
        'x-requested-with': 'apollo-client',
      },
    }),
  })), []);
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloWrapper;
